// Allocating one amount across several lines — the rule the whole discounting and tax story rests
// on.
//
// Spec: spec/06-billing-pricing-and-payments.md §1.4 ("Allocation of an order-level amount across
// lines uses the largest-remainder method so parts always sum to the whole") and §8.4 rule 2
// ("order-level discounts are allocated across lines by line total for tax").
//
// Why it matters: a ₹10 discount across three ₹100 lines is 333 + 333 + 333 = 999 paisa if each
// line rounds on its own. The missing paisa then has to live somewhere — in a rounding account, or
// in a tax total that does not match the sum of its lines, or in an invoice a player can add up
// and find wrong. Largest remainder gives the spare paisa to the line with the strongest claim to
// it and guarantees, by construction, that the parts sum to the whole.
//
// Two rules the method itself does not settle, fixed here so the C# station allocates identically:
//
//  - **Ties.** Two lines with the same remainder are ordered by position, lowest index first. The
//    order of the lines on the order is the tie-break; nothing depends on sort stability.
//  - **Sign.** A negative total is allocated by magnitude and negated, so `allocate(−x) =
//    −allocate(x)` exactly. A credit note is the negation of the invoice it reverses.
import {} from '@clavian/contracts';
import { assertInteger, DomainError } from "../errors.js";
import { assertSameCurrency, fromBigInt } from "./arithmetic.js";
/**
 * Splits `total` across `weights` so that the parts sum exactly to `total`.
 *
 * Weights are non-negative whole numbers — line totals in minor units, seconds, quantities,
 * anything proportional. When every weight is zero (an order of nothing, a free basket) the total
 * is spread evenly instead, because "sum to the whole" is not negotiable and there is no other
 * defensible share.
 */
export function allocateAmount(total, weights) {
    assertInteger(total, 'total');
    if (weights.length === 0) {
        throw new DomainError('empty-allocation', 'Cannot allocate an amount across no lines.', { total });
    }
    const effectiveWeights = normaliseWeights(weights);
    const weightTotal = effectiveWeights.reduce((sum, weight) => sum + weight, 0n);
    const negative = total < 0;
    const magnitude = BigInt(negative ? -total : total);
    const floors = [];
    const remainders = [];
    let allocated = 0n;
    for (const weight of effectiveWeights) {
        const numerator = magnitude * weight;
        const share = numerator / weightTotal;
        floors.push(share);
        remainders.push(numerator - share * weightTotal);
        allocated += share;
    }
    // Strictly fewer spare units than lines: every line lost less than one whole unit to truncation.
    let spare = Number(magnitude - allocated);
    const order = effectiveWeights
        .map((_, index) => index)
        .sort((a, b) => {
        const left = remainders[a] ?? 0n;
        const right = remainders[b] ?? 0n;
        if (left === right)
            return a - b;
        return left > right ? -1 : 1;
    });
    for (const index of order) {
        if (spare <= 0)
            break;
        floors[index] = (floors[index] ?? 0n) + 1n;
        spare -= 1;
    }
    return floors.map((share) => Number(negative ? -share : share));
}
/** The same, in money: every part carries the currency of the total. */
export function allocateMoney(total, weights) {
    return allocateAmount(total.amount, weights).map((amount) => fromBigInt(BigInt(amount), total.currency));
}
/**
 * An order-level amount allocated across lines **by line total**, which is what spec/06 §8.4 rule 2
 * calls for so that each line's tax is computed on what it actually bore.
 *
 * Line totals may be zero (a comped line, a reward); they may not be negative, because a negative
 * share of a discount is a credit nobody asked for.
 */
export function allocateMoneyByLineTotals(total, lineTotals) {
    for (const line of lineTotals) {
        assertSameCurrency(total, line);
    }
    return allocateMoney(total, lineTotals.map((line) => line.amount));
}
function normaliseWeights(weights) {
    let anyPositive = false;
    const normalised = weights.map((weight, index) => {
        assertInteger(weight, `weights[${index}]`);
        if (weight < 0) {
            throw new DomainError('out-of-range', `weights[${index}] must not be negative, got ${weight}.`, {
                index,
                weight,
            });
        }
        if (weight > 0)
            anyPositive = true;
        return BigInt(weight);
    });
    return anyPositive ? normalised : normalised.map(() => 1n);
}
//# sourceMappingURL=allocate.js.map